import { addDays, isSameDay, startOfDay, subDays, subYears } from 'date-fns';
import type { DateRange } from 'react-day-picker';

/**
 * Atalhos de período dos filtros de métricas. `today` não entra no padrão: só
 * telas que já ofereciam "Hoje" o recebem explicitamente.
 */
export type PeriodPreset = 'today' | 'last7Days' | 'last30Days' | 'last90Days' | 'last12Months';

export const DEFAULT_PERIOD_PRESETS: PeriodPreset[] = [
  'last7Days',
  'last30Days',
  'last90Days',
  'last12Months',
];

export const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
  today: 'Hoje',
  last7Days: 'Últimos 7 dias',
  last30Days: 'Últimos 30 dias',
  last90Days: 'Últimos 90 dias',
  last12Months: 'Últimos 12 meses',
};

const PRESET_DAY_LENGTH: Record<Exclude<PeriodPreset, 'last12Months'>, number> = {
  today: 1,
  last7Days: 7,
  last30Days: 30,
  last90Days: 90,
};

/**
 * Intervalo inclusivo que termina hoje, com os limites à meia-noite local — o
 * mesmo formato que o calendário entrega ao clicar num dia.
 */
export function buildPresetRange(preset: PeriodPreset, today: Date): { from: Date; to: Date } {
  const to = startOfDay(today);

  if (preset === 'last12Months') {
    return { from: addDays(subYears(to, 1), 1), to };
  }

  return { from: subDays(to, PRESET_DAY_LENGTH[preset] - 1), to };
}

/** Atalho que corresponde ao intervalo, comparando só o dia dos limites. */
export function matchPreset(
  range: DateRange | undefined,
  presets: PeriodPreset[],
  today: Date,
): PeriodPreset | null {
  if (!range?.from || !range?.to) return null;

  return (
    presets.find((preset) => {
      const candidate = buildPresetRange(preset, today);
      return isSameDay(candidate.from, range.from) && isSameDay(candidate.to, range.to);
    }) ?? null
  );
}
